import React, { useState } from 'react';
import axios from 'axios';
import { MdSearch, MdInfoOutline, MdVisibility, MdDelete } from 'react-icons/md';

const CSV_GUIDE_STEPS = [
  {
    color: '#f59e0b',
    title: 'Prepare Your CSV',
    desc: 'Required columns: tcid (unique row ID), expectedStatus (e.g. 200), and any data columns you need. Download the Sample CSV for a ready-made template.',
  },
  {
    color: '#fbbf24',
    title: 'Upload the File',
    desc: 'Click "Add Test Data" → choose your .csv file → optionally add a description → click "Upload CSV". Each row becomes one test execution.',
  },
  {
    color: '#d97706',
    title: 'Column Headers = Variables',
    desc: 'Every column name automatically becomes a {{variable}}. For example, a column called "email" is referenced as {{email}} in your API URL, headers, or body.',
  },
  {
    color: '#64748b',
    title: 'Link to a Test Step',
    desc: 'Go to Test Steps → Edit a step → assign this CSV under "Test Data File". When running with Data-Driven mode, the step executes once per CSV row.',
  },
];

function CSVQuickStartGuide() {
  const [open, setOpen] = useState(false);
  return (
    <div className="card" style={{ marginBottom: '16px', padding: '14px 18px', border: '1px solid var(--border)' }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none' }}
      >
        <MdInfoOutline color="var(--primary)" size={18} />
        <strong style={{ fontSize: '14px' }}>Test Data — Quick Start Guide</strong>
        <span style={{ marginLeft: 'auto', fontSize: '12px', color: 'var(--text-muted)' }}>{open ? '▲ Hide' : '▼ Show'}</span>
      </div>
      {open && (
        <div style={{ marginTop: '14px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '12px' }}>
          {CSV_GUIDE_STEPS.map((s, i) => (
            <div key={i} style={{
              display: 'flex', gap: '12px', alignItems: 'flex-start',
              background: 'var(--bg-input)', borderLeft: `4px solid ${s.color}`,
              borderRadius: '8px', padding: '12px',
            }}>
              <div style={{
                minWidth: '24px', height: '24px', borderRadius: '50%',
                backgroundColor: s.color, color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: '12px', flexShrink: 0,
              }}>{i + 1}</div>
              <div>
                <strong style={{ display: 'block', fontSize: '13px', marginBottom: '4px' }}>{s.title}</strong>
                <span style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.5 }}>{s.desc}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CSVUpload({ onStats }) {
  const [csvFiles, setCsvFiles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedCsv, setSelectedCsv] = useState(null);
  const [description, setDescription] = useState('');
  const [selectedFileName, setSelectedFileName] = useState('');
  const [csvToDelete, setCsvToDelete] = useState(null);
  const [deletingCsv, setDeletingCsv] = useState(false);
  const searchInputRef = React.useRef(null);
  const csvFileInputRef = React.useRef(null);
  const normalizeFileName = (value = '') => String(value).trim().toLowerCase();
  const isDuplicateSelectedFile = Boolean(
    selectedFileName && csvFiles.some((csv) => normalizeFileName(csv.fileName) === normalizeFileName(selectedFileName))
  );

  React.useEffect(() => {
    fetchCsvFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearch]);

  const fetchCsvFiles = async () => {
    try {
      const response = await axios.get('/api/csv');
      setCsvFiles(response.data);
      onStats();
    } catch (error) {
      setError('Failed to fetch CSV files: ' + error.message);
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const file = e.target.elements.csvFile.files[0];
    if (!file) {
      setError('Please select a file');
      return;
    }

    if (!file.name.endsWith('.csv')) {
      setError('Please upload a CSV file');
      return;
    }

    if (csvFiles.some((csv) => normalizeFileName(csv.fileName) === normalizeFileName(file.name))) {
      setError(`A CSV file named "${file.name}" already exists. Rename the file or delete the existing one.`);
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('description', description);

      await axios.post('/api/csv/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSuccess('CSV file uploaded successfully');
      e.target.reset();
      setDescription('');
      setSelectedFileName('');
      fetchCsvFiles();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = error.response?.data?.error || (Array.isArray(detail) ? detail[0]?.msg : detail) || error.message;
      setError('Failed to upload CSV: ' + message);
    } finally {
      setUploading(false);
    }
  };

  const openDeleteModal = (csv) => {
    setError('');
    setSuccess('');
    setCsvToDelete(csv);
  };

  const handleCsvFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    const nextFileName = file ? file.name : '';
    setSelectedFileName(nextFileName);

    if (nextFileName && csvFiles.some((csv) => normalizeFileName(csv.fileName) === normalizeFileName(nextFileName))) {
      setError(`A CSV file named "${nextFileName}" already exists. Rename the file or delete the existing one.`);
      return;
    }

    setError('');
  };

  const closeDeleteModal = () => {
    if (deletingCsv) return;
    setCsvToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!csvToDelete) return;

    setDeletingCsv(true);
    setError('');

    try {
      await axios.delete(`/api/csv/${csvToDelete.id}`);
      setSuccess(`CSV file "${csvToDelete.fileName}" deleted successfully`);
      setCsvToDelete(null);
      fetchCsvFiles();
    } catch (error) {
      setError('Failed to delete CSV: ' + error.message);
    } finally {
      setDeletingCsv(false);
    }
  };

  const handleDownloadSample = () => {
    const sampleCSV = `tcid,expectedStatus,payload
1,200,"{""email"": ""raigan.ar@gmail.com"", ""password"": ""Raigan1874@"", ""interface"": ""gui""}"
2,401,"{""email"": ""raigan.ar@gmail.com"", ""password"": ""WrongPassword123"", ""interface"": ""gui""}"
3,401,"{""email"": ""invalid@fake.com"", ""password"": ""SomePassword@123"", ""interface"": ""gui""}"
4,400,"{""email"": """", ""password"": ""SomePassword@123"", ""interface"": ""gui""}"
5,400,"{""email"": ""raigan.ar@gmail.com"", ""password"": """", ""interface"": ""gui""}"`;

    const element = document.createElement('a');
    element.setAttribute('href', 'data:text/csv;charset=utf-8,' + encodeURIComponent(sampleCSV));
    element.setAttribute('download', 'sample_csv_data.csv');
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const filteredCsvFiles = csvFiles.filter(csv => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return true;

    const searchableText = [
      csv.fileName || '',
      csv.description || '',
      String(csv.rowCount || ''),
      Array.isArray(csv.columns) ? csv.columns.join(' ') : ''
    ]
      .join(' ')
      .toLowerCase();

    return searchableText.includes(query);
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <h1 style={{ margin: 0 }}>Test Data Upload</h1>
        {showSearch ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: 'min(460px, 100%)' }}>
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by file name, description, column, or row count"
              style={{ minWidth: '220px' }}
            />
            <button
              type="button"
              className="icon-btn"
              onClick={() => setShowSearch(false)}
              aria-label="Close search"
              title="Close search"
            >
              x
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="icon-btn"
            onClick={() => setShowSearch(true)}
            aria-label="Show search"
            title="Search"
          >
            <MdSearch />
          </button>
        )}
      </div>

      <CSVQuickStartGuide />

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      <button
        type="button"
        className="btn btn-primary"
        onClick={() => setShowUploadForm((prev) => !prev)}
        style={{ marginBottom: '15px' }}
      >
        {showUploadForm ? 'Cancel' : 'Add Test Data'}
      </button>

      {showUploadForm && (
        <div className="card">
          <h2>Upload Test Data CSV</h2>
          <p>Upload CSV files containing test data and payloads. Each row in the CSV will be used as test parameters.</p>

          <form onSubmit={handleFileUpload}>
            <div className="form-group">
              <label>Select CSV File *</label>
              <input
                ref={csvFileInputRef}
                type="file"
                name="csvFile"
                accept=".csv"
                onChange={handleCsvFileChange}
                style={{ display: 'none' }}
                required
              />
              <div className="file-upload-control">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => csvFileInputRef.current && csvFileInputRef.current.click()}
                >
                  Choose CSV
                </button>
                <div className="file-upload-name">
                  {selectedFileName || 'No file selected'}
                </div>
              </div>
              <small>Supported format: CSV (Comma-Separated Values)</small>
            </div>

            <div className="form-group">
              <label>Description</label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter a description for this CSV file (optional)"
                style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid var(--border)' }}
              />
              <small>A brief description to help identify this test data</small>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button type="submit" className="btn btn-success" disabled={uploading || isDuplicateSelectedFile}>
                {uploading ? 'Uploading...' : 'Upload CSV'}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleDownloadSample}
              >
                Download Sample CSV
              </button>
            </div>
          </form>
        </div>
      )}

      <h2 style={{ marginTop: '30px' }}>Uploaded CSV Files ({filteredCsvFiles.length})</h2>
      {csvFiles.length === 0 ? (
        <div className="card">
          <p>No CSV files uploaded yet. Upload one to manage test data!</p>
        </div>
      ) : filteredCsvFiles.length === 0 ? (
        <div className="card">
          <p>No test data files found for "<strong>{searchTerm}</strong>". Try a different search.</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>File Name</th>
                <th>Rows</th>
                <th>Description</th>
                <th>Uploaded</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCsvFiles.map(csv => (
                <tr key={csv.id}>
                  <td><strong>{csv.fileName}</strong></td>
                  <td>{csv.rowCount}</td>
                  <td>{csv.description || <em style={{ color: '#999' }}>No description</em>}</td>
                  <td><small>{new Date(csv.uploadedAt).toLocaleDateString()}</small></td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-primary btn-icon-sq"
                        onClick={() => {
                          setSelectedCsv(csv);
                          setShowModal(true);
                        }}
                        title="View Data"
                      >
                        <MdVisibility />
                      </button>
                      <button
                        className="btn btn-danger btn-icon-sq"
                        onClick={() => openDeleteModal(csv)}
                        title="Delete"
                      >
                        <MdDelete />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {csvToDelete && (
        <div className="modal-overlay" onClick={closeDeleteModal}>
          <div className="modal-content modal-content-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete CSV File</h2>
              <button className="modal-close" onClick={closeDeleteModal} disabled={deletingCsv}>
                x
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this CSV file?</p>
              <div className="file-pill">
                <strong>{csvToDelete.fileName}</strong>
              </div>
              <p style={{ marginTop: '10px', color: '#d6336c' }}>
                This action cannot be undone.
              </p>
            </div>
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={closeDeleteModal} disabled={deletingCsv}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={handleConfirmDelete} disabled={deletingCsv}>
                {deletingCsv ? 'Deleting...' : 'Delete CSV'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSV Data Modal */}
      {showModal && selectedCsv && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--bg-panel)',
            borderRadius: '10px',
            padding: '20px',
            maxWidth: '90%',
            maxHeight: '80%',
            overflow: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.4)', color: 'var(--text-main)', border: '1px solid var(--border)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, color: "var(--text-main)" }}>📄 {selectedCsv.fileName}</h2>
              <button 
                onClick={() => setShowModal(false)}
                style={{
                  background: '#ff4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  padding: '8px 15px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                ✕ Close
              </button>
            </div>
            
            <p style={{ color: 'var(--text-muted)', marginBottom: '15px' }}>
              <strong>Rows:</strong> {selectedCsv.rowCount} | <strong>Columns:</strong> {selectedCsv.columns.join(', ')}
            </p>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#4a90d9', color: 'white' }}>
                    <th style={{ padding: '12px', border: '1px solid var(--border)', textAlign: 'center', color: 'white', fontWeight: 'bold' }}>#</th>
                    {selectedCsv.columns.map((col, idx) => (
                      <th key={idx} style={{ padding: '12px', border: '1px solid var(--border)', textAlign: 'left', color: 'white', fontWeight: 'bold' }}>
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {selectedCsv.data.map((row, rowIdx) => (
                    <tr key={rowIdx} style={{ backgroundColor: rowIdx % 2 === 0 ? 'var(--bg-panel-soft)' : 'var(--bg-panel)' }}>
                      <td style={{ padding: '10px', border: '1px solid var(--border)', textAlign: 'center', fontWeight: 'bold', color: 'var(--text-main)' }}>
                        {rowIdx + 1}
                      </td>
                      {selectedCsv.columns.map((col, colIdx) => (
                        <td key={colIdx} style={{ padding: '10px', border: '1px solid var(--border)', maxWidth: '400px', wordBreak: 'break-word', color: 'var(--text-main)' }}>
                          {row[col] || '-'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CSVUpload;
